import { SEA_LEVEL, VOXELS_PER_METRE, WORLD_EXTENT } from "../sim/world/constants";

/**
 * Every generator length below is written in **metres** and converted once,
 * here. The draft generator was authored against one-metre blocks; the world is
 * now eight voxels per metre (docs/ART-DIRECTION.md §0), so its *shape in
 * metres* is unchanged while its resolution is eight times finer.
 *
 * That is the whole rescale rule, and it is why `MAX_NATURAL_TERRAIN_STEP` does
 * not move: an eight-times taller hill spread over an eight-times wider
 * footprint has the same slope, so adjacent columns still step by the same
 * number of voxels.
 */
export function voxels(metres: number): number {
  return Math.round(metres * VOXELS_PER_METRE);
}

/** Region seeds are deliberately coarse: authored sites, not noise, define places. */
export const REGION_CELL_SIZE = voxels(192);
export const REGION_BLEND_WIDTH = voxels(48);

/** The sea floor rises gently for this many voxels before meeting the coast. */
export const OCEAN_SHELF_WIDTH = voxels(72);
export const COAST_BLEND_END = voxels(112);

/** A two-metre-thick, full-height wall is the world's physical outer cliff. */
export const EDGE_CLIFF_THICKNESS = voxels(2);

/**
 * Terrain away from the deliberate perimeter cliff never changes faster than
 * this, in voxels, between horizontally adjacent columns. Slope-preserving, so
 * unchanged by the micro-voxel rescale — see the note above.
 */
export const MAX_NATURAL_TERRAIN_STEP = 4;

/** Spacing of the connected underground worm network. */
export const CAVE_GRID_SIZE = voxels(96);
export const CAVE_GRID_OFFSET = voxels(48);

/**
 * Peak-to-trough amplitude, in voxels, of the surface roughness applied on top
 * of the smooth height field. This is what makes a dirt bank read as *eroded*
 * rather than as a clean staircase (docs/ART-DIRECTION.md §0.2): voxels
 * protrude and recede by one or two units. It deliberately does **not** enter
 * `sampleTerrain().height`, so structure conformance and the terrain-continuity
 * invariant keep working against the smooth surface.
 */
export const SURFACE_EROSION = 2;

/**
 * How far either side of the smooth field the *written* surface can end up, in
 * voxels, once every octave of `surfaceErosionAt` has been added and rounded.
 *
 * Anything that has to reason about the real surface rather than the smooth one
 * needs this rather than `SURFACE_EROSION`, which is only the broad octave's
 * amplitude. Two things depend on it being right: the skylight pass has to mark
 * far enough down a step's vertical face to light all of it, and a cave entrance
 * has to be carved far enough above the smooth surface that erosion cannot cap
 * it back over — and a capped entrance seals the only way into the network.
 */
export const SURFACE_EROSION_RANGE = 4;

export { SEA_LEVEL, WORLD_EXTENT };
